
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function CommercialPaintingPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Commercial Painting Cape Town | Office Building Painting | Professional Business Contractors';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional commercial painting services in Cape Town. Office buildings, retail spaces, industrial facilities. Minimal disruption, quality finishes. Free quotes available.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'commercial painting Cape Town, office painting, business painting, industrial painting, retail painting');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/commercial-painting`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Commercial Painting Services Cape Town",
      "description": "Professional commercial painting services for office buildings, retail spaces, and industrial facilities in Cape Town with minimal business disruption.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/commercial-painting`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Cape Town Professional Painters",
        "areaServed": "Cape Town, Western Cape, South Africa"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Commercial Painting Services Cape Town')) {
        existingScript.remove();
      }
    };
  }, []);

  const commercialServices = [
    {
      title: 'Office Buildings & Complexes',
      description: 'Professional painting for corporate environments that enhance productivity and brand image.',
      features: ['Reception areas', 'Conference rooms', 'Open workspaces', 'Executive offices']
    },
    {
      title: 'Retail & Hospitality',
      description: 'Create inviting customer environments that drive business and reflect your brand.',
      features: ['Storefronts', 'Dining areas', 'Customer spaces', 'Brand color matching']
    },
    {
      title: 'Industrial Facilities',
      description: 'Durable coatings for warehouses, factories, and industrial environments.',
      features: ['Warehouse floors', 'Safety markings', 'Equipment coating', 'Protective finishes']
    },
    {
      title: 'Healthcare & Education',
      description: 'Specialized paints for hospitals, schools, and institutional buildings.',
      features: ['Antimicrobial coatings', 'Easy-clean surfaces', 'Low-odor paints', 'Safety compliance']
    }
  ];

  const businessBenefits = [
    {
      title: 'Minimal Disruption',
      description: 'Flexible scheduling including after-hours and weekend work to keep your business running.',
      icon: 'ri-time-line'
    },
    {
      title: 'Professional Image',
      description: 'Fresh, professional appearance that impresses clients and motivates employees.',
      icon: 'ri-award-line'
    },
    {
      title: 'Brand Consistency',
      description: 'Precise color matching and brand guideline compliance across all locations.',
      icon: 'ri-palette-line'
    },
    {
      title: 'Maintenance Programs',
      description: 'Ongoing maintenance contracts to keep your property looking its best year-round.',
      icon: 'ri-tools-line'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b647983b9e76614b3668e3f9661e9e35.jpg"
            alt="Professional commercial painting services in Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Commercial Painting</span> Services
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Transform your business environment with expert commercial painting services in Cape Town. Our contractors deliver quality results with minimal disruption to your operations.
            </p>
            <div className="flex gap-4">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-gray-100 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Comprehensive Commercial Painting Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From small offices to large industrial complexes, our network of commercial painting specialists delivers professional results that enhance your business environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {commercialServices.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-gray-100">
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      <i className="ri-check-double-line text-green-500 mr-2 text-sm"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Why Choose Commercial Painting Specialists
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commercial contractors understand business needs and deliver solutions that work for your schedule and budget
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${benefit.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Expertise */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Industry Expertise & Specializations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our contractors have specialized experience across various commercial sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-building-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Corporate Offices</h3>
              <p className="text-gray-600">Professional environments that reflect your company culture and values while maintaining productivity.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-store-2-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Retail Spaces</h3>
              <p className="text-gray-600">Customer-focused designs that enhance shopping experiences and drive sales through strategic color use.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-hospital-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Healthcare Facilities</h3>
              <p className="text-gray-600">Antimicrobial coatings and calming colors that promote healing and meet strict health regulations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Commercial Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Streamlined process designed to minimize business disruption while delivering exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Site Assessment & Planning',
                description: 'Detailed evaluation of your space, scheduling requirements, and business needs.'
              },
              {
                step: '02',
                title: 'Flexible Scheduling',
                description: 'Work around your business hours with evening, weekend, and holiday options.'
              },
              {
                step: '03',
                title: 'Professional Execution',
                description: 'Efficient, clean work with minimal disruption to daily operations.'
              },
              {
                step: '04',
                title: 'Quality Assurance',
                description: 'Final inspection and touch-ups ensuring complete satisfaction and compliance.'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                  {process.step}
                </div>
                <h4 className="text-xl font-bold text-black mb-4">{process.title}</h4>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete your property transformation with our comprehensive painting solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link
              to="/services/interior-painting"
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-home-4-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Interior Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Professional interior solutions</p>
            </Link>

            <Link
              to="/services/exterior-painting"
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-building-2-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Exterior Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Building exterior protection</p>
            </Link>

            <Link
              to="/services/roof-painting"
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-home-8-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Roof Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Commercial roof solutions</p>
            </Link>

            <Link
              to="/services/waterproofing"
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-droplet-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Waterproofing
              </h3>
              <p className="text-gray-600 text-sm mt-2">Commercial waterproofing</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
            Ready to Transform Your Business Space?
          </h2>
          <p className="text-xl text-black/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            Connect with Cape Town's most trusted commercial painting contractors through our platform. Get up to 5 quotes and choose the perfect contractor for your business project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
